/**
 * Date helpers — ALWAYS America/Caracas for operational days (PROMPT §18).
 *
 * Never use `new Date().toISOString()` / UTC to derive the operational day:
 * a night-audit run after 20:00 VE would otherwise be filed under the wrong
 * calendar date and the engine indexes everything by `fecha`.
 *
 * Calendar dates here are pure 'YYYY-MM-DD' strings. Day-of-week / arithmetic
 * use a UTC anchor on the date string, which is timezone-independent for a
 * date-only value (no DST/offset ambiguity).
 */

export const CARACAS_TZ = 'America/Caracas';

export interface DateParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
}

const caracasFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: CARACAS_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Today's operational date in America/Caracas as 'YYYY-MM-DD'. */
export function caracasToday(now: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD.
  return caracasFormatter.format(now);
}

/** Parts of today's Caracas date. */
export function caracasTodayParts(now: Date = new Date()): DateParts {
  return parseIso(caracasToday(now));
}

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse a strict 'YYYY-MM-DD' string into parts (throws on invalid format). */
export function parseIso(iso: string): DateParts {
  const m = ISO_RE.exec(iso);
  if (!m) throw new Error(`Invalid ISO date (expected YYYY-MM-DD): ${iso}`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    throw new Error(`Invalid ISO date components: ${iso}`);
  }
  return { year, month, day };
}

/** True if `iso` is a well-formed, real calendar date. */
export function isValidIso(iso: string): boolean {
  if (!ISO_RE.test(iso)) return false;
  try {
    const { year, month, day } = parseIso(iso);
    return day <= daysInMonth(year, month);
  } catch {
    return false;
  }
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function toIso(parts: DateParts): string {
  return `${String(parts.year).padStart(4, '0')}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

/** Number of days in a given month (1-12). */
export function daysInMonth(year: number, month: number): number {
  // Day 0 of next month == last day of this month. UTC keeps it deterministic.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function yearOf(iso: string): number {
  return parseIso(iso).year;
}
export function monthOf(iso: string): number {
  return parseIso(iso).month;
}
export function dayOf(iso: string): number {
  return parseIso(iso).day;
}

/** 'YYYY-MM' month key for grouping. */
export function monthKey(iso: string): string {
  const { year, month } = parseIso(iso);
  return `${String(year).padStart(4, '0')}-${pad2(month)}`;
}

/** Last calendar day of a month as ISO. */
export function lastDayOfMonth(year: number, month: number): string {
  return toIso({ year, month, day: daysInMonth(year, month) });
}

/** First calendar day of a month as ISO. */
export function firstDayOfMonth(year: number, month: number): string {
  return toIso({ year, month, day: 1 });
}

/** Add `n` days to an ISO date (n may be negative). */
export function addDays(iso: string, n: number): string {
  const { year, month, day } = parseIso(iso);
  const d = new Date(Date.UTC(year, month - 1, day));
  d.setUTCDate(d.getUTCDate() + n);
  return toIso({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() });
}

/**
 * Signed day difference `a - b` for two ISO dates (timezone-independent via a
 * UTC anchor). Positive when `a` is after `b`, negative when before, 0 when
 * equal. Used by the date-plausibility guard (a flash's operation date should
 * be recent, never in the future).
 */
export function diffDays(aIso: string, bIso: string): number {
  const a = parseIso(aIso);
  const b = parseIso(bIso);
  const da = Date.UTC(a.year, a.month - 1, a.day);
  const db = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((da - db) / 86_400_000);
}

/**
 * Day of week for a calendar date. 1 = Monday … 7 = Sunday (ISO-8601).
 * Useful for the day-of-week × week occupancy heatmap (§10).
 */
export function isoDayOfWeek(iso: string): number {
  const { year, month, day } = parseIso(iso);
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0=Sun..6=Sat
  return dow === 0 ? 7 : dow;
}

/** True for Saturday/Sunday — used by the weekend rate rule narrative (§2.3.1). */
export function isWeekend(iso: string): boolean {
  const dow = isoDayOfWeek(iso);
  return dow === 6 || dow === 7;
}

/** Enumerate every ISO date of a month (calendar bounds, 1..last). */
export function enumerateMonthDays(year: number, month: number): string[] {
  const n = daysInMonth(year, month);
  const out: string[] = [];
  for (let d = 1; d <= n; d++) out.push(toIso({ year, month, day: d }));
  return out;
}

/** Inclusive range of ISO dates [from, to]. */
export function enumerateRange(fromIso: string, toIsoStr: string): string[] {
  if (fromIso > toIsoStr) return [];
  const out: string[] = [];
  let cur = fromIso;
  // Guard against runaway loops on malformed input.
  for (let i = 0; i < 100000 && cur <= toIsoStr; i++) {
    out.push(cur);
    cur = addDays(cur, 1);
  }
  return out;
}

/** 1-based ordinal of a day within its month (e.g. the 5th → 5). */
export function dayOfMonthOrdinal(iso: string): number {
  return dayOf(iso);
}

/** Count of days elapsed in the month up to and including `iso` (== day ordinal). */
export function daysElapsedInMonth(iso: string): number {
  return dayOf(iso);
}
